import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { useDebounceFn } from 'ahooks';
import { Icon } from 'tea-component';
import HighlightText from '@intelligence/components/HighlightText';
import { SUGGEST_DEBOUNCE_MS } from '../../_constants';
import type { IReportSearchStats, ISuggestionItem } from '../../_hooks';
import './index.scss';

interface ISearchHeaderProps {
  /** 当前搜索关键词 */
  searchKeyword: string;
  /** 搜索历史 */
  history: string[];
  /** 搜索历史加载中 */
  historyLoading?: boolean;
  /** 联想建议 */
  suggestions: ISuggestionItem[];
  /** 联想建议加载中 */
  suggestionsLoading: boolean;
  /** 统计指标 */
  stats: IReportSearchStats | null;
  /** 顶部标题（i18n 文案） */
  title: string;
  /** 提交搜索 */
  onSearch: (keyword: string) => void;
  /** 输入变化时拉联想建议 */
  onTypingChange: (input: string) => void;
  /** 删除单条搜索历史 */
  onRemoveHistory: (keyword: string) => void;
  /** 输入清空（搜索框 X） */
  onClear: () => void;
  /** 点击联想建议项 */
  onClickSuggestion?: (item: ISuggestionItem) => void;
}

const SearchHeader: React.FC<ISearchHeaderProps> = ({
  searchKeyword,
  history,
  historyLoading,
  suggestions,
  suggestionsLoading,
  stats,
  title,
  onSearch,
  onTypingChange,
  onRemoveHistory,
  onClear,
  onClickSuggestion,
}) => {
  const { formatMessage } = useIntl();

  const [inputValue, setInputValue] = useState<string>(searchKeyword || '');
  const [isFocused, setIsFocused] = useState<boolean>(false);

  /**
   * 粘滞建议：用 ref 而非 state，省掉 useEffect → setState 的额外渲染周期。
   * 父组件的 suggestions prop 变化本身已经触发了本组件重渲染，
   * 直接在渲染时同步更新 ref 即可拿到最新值，零延迟。
   */
  const stickyListRef = useRef<ISuggestionItem[]>([]);
  const stickyKeywordRef = useRef<string>('');
  const prevSuggestionsRef = useRef<ISuggestionItem[]>(suggestions);
  const prevLoadingRef = useRef<boolean>(suggestionsLoading);

  // 同步更新粘滞缓存（在渲染阶段，非 useEffect）
  // 仅在 loading 从 true→false（即一次 fetch 完成）时更新缓存，
  // 避免 clearSuggestions() 等编程式清空覆盖已有缓存。
  if (
    !suggestionsLoading &&
    prevLoadingRef.current !== suggestionsLoading
  ) {
    stickyListRef.current = suggestions;
    stickyKeywordRef.current = inputValue.trim();
  }
  prevSuggestionsRef.current = suggestions;
  prevLoadingRef.current = suggestionsLoading;

  const searchbarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (
        searchbarRef.current &&
        !searchbarRef.current.contains(e.target as Node)
      ) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setInputValue(searchKeyword || '');
  }, [searchKeyword]);

  const { run: debouncedTyping, cancel: cancelDebouncedTyping } = useDebounceFn(
    onTypingChange,
    { wait: SUGGEST_DEBOUNCE_MS },
  );

  useEffect(() => {
    return () => cancelDebouncedTyping();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const next = e.target.value;
    setInputValue(next);
    if (next.trim()) {
      debouncedTyping(next);
    } else {
      cancelDebouncedTyping();
      onClear();
    }
  };

  const handleSubmit = (): void => {
    cancelDebouncedTyping();
    onSearch((inputValue || '').trim());
    setIsFocused(false);
  };

  const handleClearInput = (): void => {
    cancelDebouncedTyping();
    setInputValue('');
    stickyListRef.current = [];
    stickyKeywordRef.current = '';
    onClear();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFocus = (): void => {
    setIsFocused(true);
  };

  const isInSuggestionMode = useMemo<boolean>(() => {
    return !!inputValue?.trim();
  }, [inputValue]);

  /**
   * 下拉是否展开：
   * - 联想模式下仅当有联想结果时才展开，无结果则完全隐藏下拉框。
   * - 历史模式下等 historyLoading 完成后再展示（避免空闪）。
   */
  const isDropdownOpen = useMemo<boolean>(() => {
    if (!isFocused) return false;
    if (isInSuggestionMode) {
      return stickyListRef.current.length > 0;
    }
    if (historyLoading) return false;
    return true;
  }, [isFocused, isInSuggestionMode, historyLoading, suggestionsLoading, suggestions]);

  const renderSuggestionContent = (): React.ReactNode => {
    const stickyList = stickyListRef.current;
    const keyword = inputValue.trim();

    return (
      <div className="dbi-report-search-header__dropdown-list">
        {stickyList.map((item, index) => (
          <div
            key={item.resource_id || `${item.text}-${index}`}
            className="dbi-report-search-header__dropdown-item"
            onClick={() => {
              cancelDebouncedTyping();
              setInputValue(item.text);
              setIsFocused(false);
              if (onClickSuggestion) {
                onClickSuggestion(item);
              } else {
                onSearch(item.text);
              }
            }}
          >
            <span className="dbi-report-search-header__dropdown-item-text">
              <HighlightText
                text={item.text}
                searchKey={keyword}
                color="var(--color-primary-5)"
                highlightClassName="dbi-report-search-header__highlight"
              />
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderHistoryContent = (): React.ReactNode => {
    if (!history.length) {
      return (
        <div className="dbi-report-search-header__dropdown-empty">
          {formatMessage({ id: 'dbi_report_search_no_recent' })}
        </div>
      );
    }
    return (
      <>
        <div className="dbi-report-search-header__dropdown-header">
          <span>{formatMessage({ id: 'dbi_report_search_recent_title' })}</span>
        </div>
        <div className="dbi-report-search-header__dropdown-list">
          {history.map((item, index) => (
            <div
              key={`${item}-${index}`}
              className="dbi-report-search-header__dropdown-item"
              onClick={() => {
                cancelDebouncedTyping();
                setInputValue(item);
                setIsFocused(false);
                onSearch(item);
              }}
            >
              <span className="dbi-report-search-header__dropdown-item-text">{item}</span>
              <Icon
                className="dbi-report-search-header__dropdown-item-remove"
                type="dismiss"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveHistory(item);
                }}
              />
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="dbi-report-search-header">
      <div className="dbi-report-search-header__inner">
        <h1 className="dbi-report-search-header__title">{title}</h1>

        <div
          ref={searchbarRef}
          className={classNames('dbi-report-search-header__searchbar', {
            'is-active': isFocused,
            'has-value': !!inputValue,
          })}
        >
          <input
            ref={inputRef}
            type="text"
            className="dbi-report-search-header__searchbar-input"
            value={inputValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            placeholder={formatMessage({ id: 'dbi_report_search_placeholder' })}
          />
          {!!inputValue && (
            <button
              type="button"
              className="dbi-report-search-header__searchbar-clear"
              onClick={handleClearInput}
            >
              <Icon type="dismiss" />
            </button>
          )}
          <button
            type="button"
            className="dbi-report-search-header__searchbar-submit"
            disabled={!inputValue?.trim()}
            onClick={handleSubmit}
          >
            <span className="dbi-report-search-header__searchbar-submit-icon" />
            <span>{formatMessage({ id: 'dbi_report_search_button' })}</span>
          </button>

          <div
            className={classNames('dbi-report-search-header__dropdown', {
              'is-visible': isDropdownOpen,
            })}
          >
            <div className="dbi-report-search-header__dropdown-body">
              {isInSuggestionMode
                ? renderSuggestionContent()
                : renderHistoryContent()}
            </div>
          </div>
        </div>

        {stats && (
          <div className="dbi-report-search-header__stats">
            <div className="dbi-report-search-header__stats-item">
              <span className="dbi-report-search-header__stats-num dbi-report-search-header__stats-num--reports">
                {Number(stats.total_reports || 0).toLocaleString()}
              </span>
              <span className="dbi-report-search-header__stats-label">
                {formatMessage({ id: 'dbi_report_search_stats_total_reports' })}
              </span>
            </div>
            <div className="dbi-report-search-header__stats-item">
              <span className="dbi-report-search-header__stats-num dbi-report-search-header__stats-num--games">
                {Number(stats.popular_games || 0).toLocaleString()}
              </span>
              <span className="dbi-report-search-header__stats-label">
                {formatMessage({ id: 'dbi_report_search_stats_popular_games' })}
              </span>
            </div>
            <div className="dbi-report-search-header__stats-item">
              <span className="dbi-report-search-header__stats-num dbi-report-search-header__stats-num--vendors">
                {Number(stats.data_sources || 0).toLocaleString()}
              </span>
              <span className="dbi-report-search-header__stats-label">
                {formatMessage({ id: 'dbi_report_search_stats_data_sources' })}
              </span>
            </div>
            <div className="dbi-report-search-header__stats-item">
              <span className="dbi-report-search-header__stats-num dbi-report-search-header__stats-num--practices">
                {Number(stats.ai_practices || 0).toLocaleString()}
              </span>
              <span className="dbi-report-search-header__stats-label">
                {formatMessage({ id: 'dbi_report_search_stats_ai_practices' })}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(SearchHeader);
